/**
 * Creative Engine Benchmark — OWNER-ONLY diagnostics page.
 *
 * Located at /admin/creative-benchmark. Guarded on the backend
 * by the ``ADMIN_EMAILS`` allow-list. On 403 we hide the entire
 * page so normal users see the same "not found" experience.
 *
 * Nothing on this page touches production creative routing.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Beaker,
  Play,
  Loader2,
  Trophy,
  History as HistoryIcon,
  ChevronRight,
  AlertTriangle,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import GlassCard from "@/components/GlassCard";
import GradientMesh from "@/components/GradientMesh";

const PLATFORMS = ["Instagram", "LinkedIn", "Facebook", "X"];
const CONTENT_FORMATS = [
  { value: "auto", label: "Auto" },
  { value: "standard", label: "Standard" },
  { value: "meme", label: "Meme" },
  { value: "promotion", label: "Promotion" },
  { value: "educational", label: "Educational" },
  { value: "announcement", label: "Announcement" },
];
const TONES = [
  "Match brand", "Professional", "Conversational", "Bold",
  "Playful", "Sarcastic", "Warm", "Premium",
];
const SCORE_DIMENSIONS = [
  ["creativity", "Creativity"],
  ["instruction", "Instruction following"],
  ["humor", "Humor"],
  ["caption", "Caption quality"],
  ["image_concept", "Image concept quality"],
  ["brand_fit", "Brand fit"],
  ["platform_fit", "Platform fit"],
  ["human", "Human / non-robotic feel"],
];
const PROVIDER_LABELS = {
  current: "Current Postmoro",
  openai: "OpenAI",
  anthropic: "Claude",
};
const PROVIDER_KEYS = ["current", "openai", "anthropic"];

export default function CreativeBenchmarkPage() {
  const navigate = useNavigate();

  // Owner-guard state.
  const [meta, setMeta] = useState(null);
  const [gateLoading, setGateLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);

  // Inputs.
  const [prompt, setPrompt] = useState("");
  const [platforms, setPlatforms] = useState(["Instagram", "LinkedIn"]);
  const [contentFormat, setContentFormat] = useState("auto");
  const [tone, setTone] = useState("Match brand");
  const [includeBrand, setIncludeBrand] = useState(true);
  const [includeRecent, setIncludeRecent] = useState(true);

  // Run state.
  const [running, setRunning] = useState(false);
  const [currentRun, setCurrentRun] = useState(null); // { run_id, providers, context_summary }

  // History.
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Owner scoring on the CURRENT run.
  const [scores, setScores] = useState({}); // { provider: { dim: 1-5 } }
  const [winner, setWinner] = useState("");
  const [notes, setNotes] = useState("");
  const [savingScore, setSavingScore] = useState(false);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await api.get("/admin/creative-benchmark/history");
      setHistory(res?.data?.items || []);
    } catch (_e) {
      /* silent — history is optional */
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const res = await api.get("/admin/creative-benchmark/meta");
        if (!live) return;
        setMeta(res.data);
        setForbidden(false);
        await loadHistory();
      } catch (err) {
        if (!live) return;
        if (err?.response?.status === 403) {
          setForbidden(true);
        } else if (err?.response?.status === 401) {
          navigate("/login");
        } else {
          toast.error(formatApiError(err, "Could not load benchmark meta."));
        }
      } finally {
        if (live) setGateLoading(false);
      }
    })();
    return () => {
      live = false;
    };
  }, [navigate, loadHistory]);

  const togglePlatform = (p) => {
    setPlatforms((prev) => (prev.includes(p)
      ? prev.filter((x) => x !== p)
      : [...prev, p]));
  };

  const runBenchmark = async () => {
    if (!prompt.trim()) {
      toast.error("Enter a prompt first");
      return;
    }
    setRunning(true);
    setCurrentRun(null);
    setScores({});
    setWinner("");
    setNotes("");
    try {
      const res = await api.post("/admin/creative-benchmark/run", {
        prompt,
        platforms,
        content_format: contentFormat,
        tone,
        include_brand_learning: includeBrand,
        include_recent_content: includeRecent,
      });
      setCurrentRun(res.data);
      toast.success("Benchmark complete");
      loadHistory();
    } catch (err) {
      if (err?.response?.status === 403) {
        setForbidden(true);
      } else {
        toast.error(formatApiError(err, "Benchmark failed."));
      }
    } finally {
      setRunning(false);
    }
  };

  const setScore = (provider, dim, val) => {
    setScores((prev) => ({
      ...prev,
      [provider]: { ...(prev[provider] || {}), [dim]: val },
    }));
  };

  const saveScores = async () => {
    if (!currentRun?.run_id) return;
    setSavingScore(true);
    try {
      await api.post(`/admin/creative-benchmark/${currentRun.run_id}/score`, {
        scores,
        winner: winner || null,
        notes: notes || null,
      });
      toast.success("Scores saved");
      loadHistory();
    } catch (err) {
      toast.error(formatApiError(err, "Save failed."));
    } finally {
      setSavingScore(false);
    }
  };

  const openStarterPrompt = (p) => {
    setPrompt(p);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const openHistoryRun = async (runId) => {
    try {
      const res = await api.get(`/admin/creative-benchmark/${runId}`);
      const doc = res.data || {};
      setCurrentRun({
        run_id: doc.run_id,
        providers: doc.providers,
        context_summary: doc.context_summary,
      });
      setPrompt(doc.request?.prompt || "");
      setScores(doc.scores || {});
      setWinner(doc.winner || "");
      setNotes(doc.notes || "");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err, "Could not open run."));
    }
  };

  // ---- Rendering guards ----
  if (gateLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="creative-benchmark-loading">
        <Loader2 className="h-6 w-6 animate-spin text-[color:var(--text-3)]" />
      </div>
    );
  }
  if (forbidden) {
    return (
      <div className="max-w-lg mx-auto py-16 text-center" data-testid="creative-benchmark-forbidden">
        <AlertTriangle className="h-10 w-10 mx-auto text-[color:var(--text-3)] mb-3" />
        <div className="font-display text-2xl font-semibold mb-2">Not found</div>
        <div className="text-sm text-[color:var(--text-3)]">
          This page is available to Postmoro owners only.
        </div>
      </div>
    );
  }

  return (
    <div className="relative" data-testid="creative-benchmark-page">
      <GradientMesh />
      <div className="relative">
        <PageHeader
          title="Creative Engine Benchmark"
          subtitle="Compare CURRENT Postmoro, OpenAI, and Claude on the same prompt + business context. Owner-only. No production behaviour changes."
          testidPrefix="creative-benchmark"
        />

        {/* Provider readiness banner */}
        <GlassCard className="p-4 mb-5" data-testid="creative-benchmark-providers-card">
          <div className="flex items-center gap-2 mb-2">
            <Beaker className="h-4 w-4 text-[color:var(--brand-primary)]" />
            <div className="text-sm font-semibold">Providers</div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
            {PROVIDER_KEYS.map((k) => {
              const p = meta?.providers?.[k];
              const configured = p?.configured;
              return (
                <div
                  key={k}
                  className="rounded-lg border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-3"
                  data-testid={`creative-benchmark-provider-status-${k}`}
                >
                  <div className="font-semibold">{PROVIDER_LABELS[k]}</div>
                  <div className="text-[color:var(--text-3)] mt-1">
                    {p?.model || "—"}
                  </div>
                  <div className={"mt-1 " + (configured
                    ? "text-emerald-400"
                    : "text-amber-400")}
                  >
                    {configured ? "Configured" : "Not configured"}
                  </div>
                </div>
              );
            })}
          </div>
          {meta?.secret_required ? (
            <div className="mt-2 text-[10px] text-[color:var(--text-3)]" data-testid="creative-benchmark-secret-required">
              Secret required: {meta.secret_required}
            </div>
          ) : null}
        </GlassCard>

        {/* Inputs */}
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-4 mb-5">
          <GlassCard className="p-5" data-testid="creative-benchmark-input-card">
            <Label className="text-xs text-[color:var(--text-3)]" htmlFor="cb-prompt">
              Main prompt
            </Label>
            <Textarea
              id="cb-prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value.slice(0, 4000))}
              placeholder="e.g. Create a funny meme for Postmoro about business owners struggling to stay consistent on social media…"
              className="mt-1 min-h-[130px] bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
              data-testid="creative-benchmark-prompt"
            />
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-[color:var(--text-3)]">Content format</Label>
                <Select value={contentFormat} onValueChange={setContentFormat}>
                  <SelectTrigger className="mt-1" data-testid="creative-benchmark-format">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTENT_FORMATS.map((f) => (
                      <SelectItem key={f.value} value={f.value} data-testid={`creative-benchmark-format-${f.value}`}>
                        {f.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-[color:var(--text-3)]">Tone</Label>
                <Select value={tone} onValueChange={setTone}>
                  <SelectTrigger className="mt-1" data-testid="creative-benchmark-tone">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TONES.map((t) => (
                      <SelectItem key={t} value={t} data-testid={`creative-benchmark-tone-${t}`}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-3">
              <Label className="text-xs text-[color:var(--text-3)]">Platforms</Label>
              <div className="flex flex-wrap gap-2 mt-1" data-testid="creative-benchmark-platforms">
                {PLATFORMS.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => togglePlatform(p)}
                    className={
                      "text-xs rounded-full px-3 py-1 border transition-colors " +
                      (platforms.includes(p)
                        ? "bg-[color:var(--brand-primary)] text-[color:var(--bg-0)] border-[color:var(--brand-primary)]"
                        : "bg-[color:var(--surface-2)] text-[color:var(--text-2)] border-[color:var(--border-subtle)]")
                    }
                    data-testid={`creative-benchmark-platform-${p}`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
              <label className="flex items-center gap-2 text-xs text-[color:var(--text-2)]" data-testid="creative-benchmark-brand-toggle-row">
                <Switch
                  checked={includeBrand}
                  onCheckedChange={setIncludeBrand}
                  data-testid="creative-benchmark-brand-toggle"
                />
                Include Brand Learning
              </label>
              <label className="flex items-center gap-2 text-xs text-[color:var(--text-2)]" data-testid="creative-benchmark-recent-toggle-row">
                <Switch
                  checked={includeRecent}
                  onCheckedChange={setIncludeRecent}
                  data-testid="creative-benchmark-recent-toggle"
                />
                Include recent-content context
              </label>
            </div>
            <div className="mt-4">
              <Button
                onClick={runBenchmark}
                disabled={running || !prompt.trim()}
                className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold"
                data-testid="creative-benchmark-run-btn"
              >
                {running ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Play className="h-4 w-4 mr-2" />
                )}
                Run benchmark
              </Button>
            </div>
          </GlassCard>

          {/* Starter prompts */}
          <GlassCard className="p-4" data-testid="creative-benchmark-starter-card">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="h-4 w-4 text-[color:var(--brand-primary)]" />
              <div className="text-sm font-semibold">Suggested prompts</div>
            </div>
            <div className="space-y-1.5 max-h-[420px] overflow-y-auto pr-1" data-testid="creative-benchmark-starter-list">
              {(meta?.starter_prompts || []).map((sp, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => openStarterPrompt(sp.prompt)}
                  className="w-full text-left text-xs px-3 py-2 rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] hover:border-[color:var(--brand-primary)] transition-colors"
                  data-testid={`creative-benchmark-starter-${i}`}
                >
                  <div className="font-medium text-[color:var(--text-1)]">
                    {i + 1}. {sp.label}
                  </div>
                  <div className="text-[10px] text-[color:var(--text-3)] mt-0.5 line-clamp-2">
                    {sp.prompt}
                  </div>
                </button>
              ))}
            </div>
          </GlassCard>
        </div>

        {/* Results */}
        {currentRun ? (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-5" data-testid="creative-benchmark-results-grid">
              {PROVIDER_KEYS.map((k) => (
                <ProviderColumn
                  key={k}
                  providerKey={k}
                  data={currentRun.providers?.[k]}
                  score={scores[k] || {}}
                  onScore={setScore}
                />
              ))}
            </div>

            {/* Winner + notes + save */}
            <GlassCard className="p-4 mb-6" data-testid="creative-benchmark-verdict-card">
              <div className="flex items-center gap-2 mb-3">
                <Trophy className="h-4 w-4 text-[color:var(--brand-primary)]" />
                <div className="text-sm font-semibold">Owner verdict</div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 mb-3">
                {[
                  ["current", "Current"],
                  ["openai", "OpenAI"],
                  ["anthropic", "Claude"],
                  ["tie", "Tie"],
                ].map(([val, label]) => (
                  <button
                    key={val}
                    type="button"
                    onClick={() => setWinner(val)}
                    className={
                      "text-xs px-3 py-2 rounded-md border transition-colors " +
                      (winner === val
                        ? "bg-[color:var(--brand-primary)] text-[color:var(--bg-0)] border-[color:var(--brand-primary)]"
                        : "bg-[color:var(--surface-2)] text-[color:var(--text-2)] border-[color:var(--border-subtle)]")
                    }
                    data-testid={`creative-benchmark-winner-${val}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <Label className="text-xs text-[color:var(--text-3)]">Notes</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value.slice(0, 4000))}
                placeholder="Optional — why did this one win? Any patterns you noticed?"
                className="mt-1 min-h-[80px] bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
                data-testid="creative-benchmark-notes"
              />
              <Button
                onClick={saveScores}
                disabled={savingScore || !currentRun?.run_id}
                className="mt-3 bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold"
                data-testid="creative-benchmark-save-scores"
              >
                {savingScore ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Save scores
              </Button>
            </GlassCard>
          </>
        ) : null}

        {/* History */}
        <GlassCard className="p-4" data-testid="creative-benchmark-history-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <HistoryIcon className="h-4 w-4 text-[color:var(--brand-primary)]" />
              <div className="text-sm font-semibold">Benchmark history</div>
            </div>
            {historyLoading ? (
              <Loader2 className="h-3 w-3 animate-spin text-[color:var(--text-3)]" />
            ) : null}
          </div>
          {history.length === 0 ? (
            <div className="text-xs text-[color:var(--text-3)] py-4" data-testid="creative-benchmark-history-empty">
              No runs yet — hit "Run benchmark" above to compare providers on your first prompt.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="creative-benchmark-history-table">
                <thead>
                  <tr className="text-[color:var(--text-3)] border-b border-[color:var(--border-subtle)]">
                    <th className="text-left py-2 pr-2">Date</th>
                    <th className="text-left py-2 pr-2">Prompt</th>
                    <th className="text-left py-2 pr-2">Content format</th>
                    <th className="text-left py-2 pr-2">Providers</th>
                    <th className="text-left py-2 pr-2">Winner</th>
                    <th className="text-right py-2">Open</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((row) => (
                    <tr key={row.run_id} className="border-b border-[color:var(--border-subtle)] hover:bg-[color:var(--surface-2)]">
                      <td className="py-2 pr-2 text-[color:var(--text-3)] whitespace-nowrap">
                        {new Date(row.created_at).toLocaleString()}
                      </td>
                      <td className="py-2 pr-2 max-w-[320px] truncate" title={row.prompt_snippet}>
                        {row.prompt_snippet}
                      </td>
                      <td className="py-2 pr-2 uppercase text-[10px] tracking-wider text-[color:var(--text-3)]">
                        {row.content_format || "auto"}
                      </td>
                      <td className="py-2 pr-2 text-[10px]">
                        {PROVIDER_KEYS.map((k) => (
                          <span key={k} className="mr-2">
                            {PROVIDER_LABELS[k]}: {row.provider_statuses?.[k] || "—"}
                          </span>
                        ))}
                      </td>
                      <td className="py-2 pr-2 font-semibold">
                        {row.winner ? PROVIDER_LABELS[row.winner] || row.winner : "—"}
                      </td>
                      <td className="py-2 text-right">
                        <button
                          type="button"
                          onClick={() => openHistoryRun(row.run_id)}
                          className="text-[color:var(--brand-primary)] hover:underline"
                          data-testid={`creative-benchmark-history-open-${row.run_id}`}
                        >
                          <ChevronRight className="h-4 w-4 inline" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </GlassCard>
      </div>
    </div>
  );
}


function ProviderColumn({ providerKey, data, score, onScore }) {
  const status = data?.status || "unknown";
  const result = data?.result || {};
  const statusColor = status === "ok"
    ? "text-emerald-400"
    : status === "not_configured"
      ? "text-amber-400"
      : "text-rose-400";
  const label = PROVIDER_LABELS[providerKey];
  return (
    <GlassCard className="p-4" data-testid={`creative-benchmark-column-${providerKey}`}>
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="font-display text-sm font-semibold">{label}</div>
          <div className="text-[10px] text-[color:var(--text-3)]">{data?.model || "—"}</div>
        </div>
        <div className={"text-[10px] uppercase tracking-wider font-semibold " + statusColor}>
          {status}
        </div>
      </div>
      {status !== "ok" ? (
        <div className="text-xs text-[color:var(--text-3)] py-2" data-testid={`creative-benchmark-column-${providerKey}-error`}>
          {data?.provider_error || "No result."}
        </div>
      ) : (
        <div className="space-y-2 text-xs">
          <Field label="Core concept" value={result.core_concept} />
          <Field label="Hook" value={result.hook} />
          <Field label="Caption" value={result.caption} textarea />
          <Field label="CTA" value={result.cta} />
          <Field
            label="Hashtags"
            value={Array.isArray(result.hashtags) ? result.hashtags.join(" ") : result.hashtags}
          />
          <Field label="Image concept" value={result.image_concept} />
          <Field label="Image subject" value={result.image_subject} />
          <Field label="Image mood" value={result.image_mood} />
          <Field label="Visual direction" value={result.visual_direction} />
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[color:var(--text-3)]">Text on image</div>
            <div className="mt-0.5">
              <div>top: <span className="text-[color:var(--text-1)]">{result.text_on_image?.top || "—"}</span></div>
              <div>bottom: <span className="text-[color:var(--text-1)]">{result.text_on_image?.bottom || "—"}</span></div>
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[color:var(--text-3)]">Platform variants</div>
            <div className="mt-0.5 space-y-1">
              {["instagram", "linkedin", "facebook", "x"].map((p) => (
                <div key={p}>
                  <span className="text-[color:var(--text-3)]">{p}:</span>{" "}
                  <span className="text-[color:var(--text-1)]">{result.platform_variants?.[p] || "—"}</span>
                </div>
              ))}
            </div>
          </div>
          <Field label="Reasoning summary" value={result.creative_reasoning_summary} />
        </div>
      )}

      {/* Metrics */}
      <div className="mt-3 pt-3 border-t border-[color:var(--border-subtle)] text-[10px] text-[color:var(--text-3)] grid grid-cols-2 gap-1">
        <div>latency: {data?.latency_ms ?? "—"} ms</div>
        <div>input tok: {data?.input_tokens ?? "—"}</div>
        <div>output tok: {data?.output_tokens ?? "—"}</div>
        <div>cost est: {data?.estimated_cost_usd != null ? `$${data.estimated_cost_usd}` : "unavailable"}</div>
        <div>parse: {data?.parse_success ? "ok" : "—"}</div>
        <div>schema: {data?.schema_valid ? "ok" : (data?.schema_error || "—")}</div>
      </div>

      {/* Scoring */}
      <div className="mt-3 pt-3 border-t border-[color:var(--border-subtle)]" data-testid={`creative-benchmark-column-${providerKey}-scoring`}>
        <div className="text-[10px] uppercase tracking-wider text-[color:var(--text-3)] font-semibold mb-2">
          Owner scoring
        </div>
        <div className="space-y-1">
          {SCORE_DIMENSIONS.map(([dim, label]) => (
            <div key={dim} className="flex items-center justify-between text-[11px]">
              <span className="text-[color:var(--text-2)]">{label}</span>
              <div className="flex gap-0.5">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => onScore(providerKey, dim, n)}
                    className={
                      "w-5 h-5 text-[10px] rounded border transition-colors " +
                      (score[dim] === n
                        ? "bg-[color:var(--brand-primary)] text-[color:var(--bg-0)] border-[color:var(--brand-primary)]"
                        : "bg-[color:var(--surface-2)] text-[color:var(--text-3)] border-[color:var(--border-subtle)]")
                    }
                    data-testid={`creative-benchmark-score-${providerKey}-${dim}-${n}`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </GlassCard>
  );
}


function Field({ label, value, textarea }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[color:var(--text-3)]">{label}</div>
      <div className={"mt-0.5 text-[color:var(--text-1)] " + (textarea ? "whitespace-pre-wrap" : "")}>
        {value || <span className="text-[color:var(--text-3)]">—</span>}
      </div>
    </div>
  );
}
